import React from 'react';

import { Optional } from 'services/base/types';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';

import { PlanType } from 'services/accounts/types';
import { getPlanTypeSubtitle, getPlanTypeTitle } from 'services/accounts/logic/plans/plans';

import { getPlanTypeIcon, themedPlanTypeIconImageStyles } from 'assets/enums/plan_types/plan_types';

import heroIconImageWarningMidnight from '../../assets/heroes/warning/dark.png';
import heroIconImageWarningDaylight from '../../assets/heroes/warning/light.png';
import heroIconImageStatusGoodMidnight from '../../assets/heroes/status_good/dark.png';
import heroIconImageStatusGoodDaylight from '../../assets/heroes/status_good/light.png';
import heroIconImageStatusWarnMidnight from '../../assets/heroes/status_warn/dark.png';
import heroIconImageStatusWarnDaylight from '../../assets/heroes/status_warn/light.png';

import fieldIconImageTrialMidnight from '../../assets/fields/trial/dark.png';
import fieldIconImageTrialDaylight from '../../assets/fields/trial/light.png';
import fieldIconImageLicenseMidnight from '../../assets/fields/billing_license/dark.png';
import fieldIconImageLicenseDaylight from '../../assets/fields/billing_license/light.png';
import fieldIconImageSubscriptionMidnight from '../../assets/fields/billing_subscription/dark.png';
import fieldIconImageSubscriptionDaylight from '../../assets/fields/billing_subscription/light.png';
import fieldIconImageDurationMidnight from '../../assets/fields/duration/dark.png';
import fieldIconImageDurationDaylight from '../../assets/fields/duration/light.png';
import fieldIconImagePaymentMidnight from '../../assets/fields/payment/dark.png';
import fieldIconImagePaymentDaylight from '../../assets/fields/payment/light.png';
import fieldIconImageInvoiceMidnight from '../../assets/fields/invoice/dark.png';
import fieldIconImageInvoiceDaylight from '../../assets/fields/invoice/light.png';
import fieldIconImageStatusGoodMidnight from '../../assets/fields/status_good/dark.png';
import fieldIconImageStatusGoodDaylight from '../../assets/fields/status_good/light.png';
import fieldIconImageStatusWarnMidnight from '../../assets/fields/status_warn/dark.png';
import fieldIconImageStatusWarnDaylight from '../../assets/fields/status_warn/light.png';

import baseStyles from './plan_item_base.module.css';

export type PlanItemStyles = {
  plan: string;
  planHeader: string;
  planHeaderIcon: string;
  planHeaderText: string;
  planHeaderInfo: string;
  planContent: string;

  hero: string;
  heroBig: string;
  heroIcon: string;
  heroHead: string;
  heroTitle: string;
  heroText: string;
  heroDetail: string;

  field: string;
  fieldPart: string;
  fieldIcon: string;
  fieldAlert: string;
  fieldTextLeft: string;
  fieldTextRight: string;
  fieldCharge: string;
  fieldAmount: string;
  fieldInfo: string;

  item: string;

  modalContainer: string;
  modalContainerBig: string;
  modalContainerLarge: string;
  modalContainerLargest: string;
  modalContainerPopOut: string;
  modalHeaderRow: string;
  modalContentRow: string;
  modalCloseIcon: string;

  section: string;
  sectionTitle: string;

  clickable: string;

  cardDefault: string;
  cardDisabled: string;
  cardSelected: string;
  cardIcon: string;
  cardText: string;
  cardName: string;
  cardInfo: string;
  cardTags: string;

  currencies: string;

  notes: string;

  link: string;

  alertCritical: string;
  alertWarning: string;

  warning: string;
  spacer: string;
  open: string;
  add: string;

  heroIconImageWarning: string;
  heroIconImageStatusGood: string;
  heroIconImageStatusWarn: string;

  fieldIconImageTrial: string;
  fieldIconImageLicense: string;
  fieldIconImageSubscription: string;
  fieldIconImageDuration: string;
  fieldIconImagePayment: string;
  fieldIconImageInvoice: string;
  fieldIconImageStatusGood: string;
  fieldIconImageStatusWarn: string;
};

export const themedPlanItemStyles: ThemedStyles<PlanItemStyles> = {
  [Theme.DAYLIGHT]: {
    plan: baseStyles.daylightPlan,
    planHeader: baseStyles.planHeader,
    planHeaderIcon: baseStyles.planHeaderIcon,
    planHeaderText: baseStyles.planHeaderText,
    planHeaderInfo: baseStyles.planHeaderInfo,
    planContent: baseStyles.planContent,

    hero: baseStyles.hero,
    heroBig: baseStyles.heroBig,
    heroIcon: baseStyles.heroIcon,
    heroHead: baseStyles.heroHead,
    heroTitle: baseStyles.heroTitle,
    heroText: baseStyles.heroText,
    heroDetail: baseStyles.heroDetail,

    field: baseStyles.field,
    fieldPart: baseStyles.fieldPart,
    fieldIcon: baseStyles.fieldIcon,
    fieldAlert: baseStyles.fieldAlert,
    fieldTextLeft: baseStyles.fieldTextLeft,
    fieldTextRight: baseStyles.fieldTextRight,
    fieldCharge: baseStyles.fieldCharge,
    fieldAmount: baseStyles.fieldAmount,
    fieldInfo: baseStyles.fieldInfo,

    item: baseStyles.daylightItem,

    modalContainer: baseStyles.modalContainer,
    modalContainerBig: baseStyles.modalContainerBig,
    modalContainerLarge: baseStyles.modalContainerLarge,
    modalContainerLargest: baseStyles.modalContainerLargest,
    modalContainerPopOut: baseStyles.modalContainerPopOut,
    modalHeaderRow: baseStyles.modalHeaderRow,
    modalContentRow: baseStyles.modalContentRow,
    modalCloseIcon: baseStyles.daylightModalCloseIcon,

    section: baseStyles.section,
    sectionTitle: baseStyles.daylightSectionTitle,

    clickable: baseStyles.clickable,

    cardDefault: baseStyles.daylightCardDefault,
    cardDisabled: baseStyles.daylightCardDisabled,
    cardSelected: baseStyles.daylightCardSelected,
    cardIcon: baseStyles.cardIcon,
    cardText: baseStyles.cardText,
    cardName: baseStyles.cardName,
    cardInfo: baseStyles.cardInfo,
    cardTags: baseStyles.daylightCardTags,

    currencies: baseStyles.currencies,

    notes: baseStyles.notes,

    link: baseStyles.link,

    alertCritical: baseStyles.alertCritical,
    alertWarning: baseStyles.alertWarning,

    warning: baseStyles.daylightWarning,
    spacer: baseStyles.spacer,
    open: baseStyles.daylightOpen,
    add: baseStyles.daylightAdd,

    heroIconImageWarning: heroIconImageWarningDaylight,
    heroIconImageStatusGood: heroIconImageStatusGoodDaylight,
    heroIconImageStatusWarn: heroIconImageStatusWarnDaylight,

    fieldIconImageTrial: fieldIconImageTrialDaylight,
    fieldIconImageLicense: fieldIconImageLicenseDaylight,
    fieldIconImageSubscription: fieldIconImageSubscriptionDaylight,
    fieldIconImageDuration: fieldIconImageDurationDaylight,
    fieldIconImagePayment: fieldIconImagePaymentDaylight,
    fieldIconImageInvoice: fieldIconImageInvoiceDaylight,
    fieldIconImageStatusGood: fieldIconImageStatusGoodDaylight,
    fieldIconImageStatusWarn: fieldIconImageStatusWarnDaylight,
  },
  [Theme.MIDNIGHT]: {
    plan: baseStyles.midnightPlan,
    planHeader: baseStyles.planHeader,
    planHeaderIcon: baseStyles.planHeaderIcon,
    planHeaderText: baseStyles.planHeaderText,
    planHeaderInfo: baseStyles.planHeaderInfo,
    planContent: baseStyles.planContent,

    hero: baseStyles.hero,
    heroBig: baseStyles.heroBig,
    heroIcon: baseStyles.heroIcon,
    heroHead: baseStyles.heroHead,
    heroTitle: baseStyles.heroTitle,
    heroText: baseStyles.heroText,
    heroDetail: baseStyles.heroDetail,

    field: baseStyles.field,
    fieldPart: baseStyles.fieldPart,
    fieldIcon: baseStyles.fieldIcon,
    fieldAlert: baseStyles.fieldAlert,
    fieldTextLeft: baseStyles.fieldTextLeft,
    fieldTextRight: baseStyles.fieldTextRight,
    fieldCharge: baseStyles.fieldCharge,
    fieldAmount: baseStyles.fieldAmount,
    fieldInfo: baseStyles.fieldInfo,

    item: baseStyles.midnightItem,

    modalContainer: baseStyles.modalContainer,
    modalContainerBig: baseStyles.modalContainerBig,
    modalContainerLarge: baseStyles.modalContainerLarge,
    modalContainerLargest: baseStyles.modalContainerLargest,
    modalContainerPopOut: baseStyles.modalContainerPopOut,
    modalHeaderRow: baseStyles.modalHeaderRow,
    modalContentRow: baseStyles.modalContentRow,
    modalCloseIcon: baseStyles.midnightModalCloseIcon,

    section: baseStyles.section,
    sectionTitle: baseStyles.midnightSectionTitle,

    clickable: baseStyles.clickable,

    cardDefault: baseStyles.midnightCardDefault,
    cardDisabled: baseStyles.midnightCardDisabled,
    cardSelected: baseStyles.midnightCardSelected,
    cardIcon: baseStyles.cardIcon,
    cardText: baseStyles.cardText,
    cardName: baseStyles.cardName,
    cardInfo: baseStyles.cardInfo,
    cardTags: baseStyles.midnightCardTags,

    currencies: baseStyles.currencies,

    notes: baseStyles.notes,

    link: baseStyles.link,

    alertCritical: baseStyles.alertCritical,
    alertWarning: baseStyles.alertWarning,

    warning: baseStyles.midnightWarning,
    spacer: baseStyles.spacer,
    open: baseStyles.midnightOpen,
    add: baseStyles.midnightAdd,

    heroIconImageWarning: heroIconImageWarningMidnight,
    heroIconImageStatusGood: heroIconImageStatusGoodMidnight,
    heroIconImageStatusWarn: heroIconImageStatusWarnMidnight,

    fieldIconImageTrial: fieldIconImageTrialMidnight,
    fieldIconImageLicense: fieldIconImageLicenseMidnight,
    fieldIconImageSubscription: fieldIconImageSubscriptionMidnight,
    fieldIconImageDuration: fieldIconImageDurationMidnight,
    fieldIconImagePayment: fieldIconImagePaymentMidnight,
    fieldIconImageInvoice: fieldIconImageInvoiceMidnight,
    fieldIconImageStatusGood: fieldIconImageStatusGoodMidnight,
    fieldIconImageStatusWarn: fieldIconImageStatusWarnMidnight,
  },
};

type PlanItemBaseProps = {
  type: PlanType;
  children: React.ReactNode;
  id: Optional<string>;
  onHeaderClick?: () => void;
};

export const PlanItemBase = ({ type, children, id, onHeaderClick }: PlanItemBaseProps) => {
  const styles = useStyles(themedPlanItemStyles);
  const planTypeIconImages = useStyles(themedPlanTypeIconImageStyles);
  const planTypeIconImage = getPlanTypeIcon(planTypeIconImages, type);
  const planTypeTitle = getPlanTypeTitle(type);
  const planTypeSubtitle = getPlanTypeSubtitle(type);
  return (
    <div id={id} className={styles.plan}>
      <div className={styles.planHeader} onClick={onHeaderClick}>
        <img className={styles.planHeaderIcon} src={planTypeIconImage} alt='Plan Icon' />
        <div className={styles.planHeaderText}>
          {planTypeTitle}
          <div className={styles.planHeaderInfo}>
            {planTypeSubtitle}
          </div>
        </div>
        <div className={styles.open}/>
      </div>
      { children }
    </div>
  );
};
